import { cn } from '@/lib/utils';

/**
 * Canonical money formatter for the underwriting panels and admin console —
 * `R 1 234 567.89` via the `en-ZA` locale, two fraction digits. This is the
 * single source of truth for that format; the referral / bind / facility /
 * authority surfaces all read from here instead of re-deriving the string.
 *
 * Multi-currency workbench and PDF figures use a separate symbol-driven
 * formatter in `utils/formatters.ts` (different locale grouping); the two are
 * deliberately distinct and should not be merged.
 */
export const formatMoney = (
  amount: number,
  options: { symbol?: string; fractionDigits?: number } = {},
): string => {
  const { symbol = 'R', fractionDigits = 2 } = options;
  return `${symbol} ${amount.toLocaleString('en-ZA', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

interface CurrencyValueProps {
  readonly amount: number;
  readonly symbol?: string;
  readonly fractionDigits?: number;
  readonly className?: string;
}

/**
 * Renders a money amount at the display edge with `tabular-nums`, so figures
 * align in columns and digits don't jitter as values change. Weight, colour,
 * and size stay with the caller — money stands out through alignment and
 * weight, not bespoke per-screen styling.
 */
export function CurrencyValue({ amount, symbol, fractionDigits, className }: CurrencyValueProps) {
  return (
    <span className={cn('tabular-nums', className)}>
      {formatMoney(amount, { symbol, fractionDigits })}
    </span>
  );
}
